// The functions fast-formula-parser DECLARES AND NEVER IMPLEMENTED.
//
// The library ships 280 formulas, and a couple of dozen of them are empty
// bodies — `MIN: () => {}` — which the dispatcher turns into "Function MIN is
// not implemented" at evaluation time. Not obscure ones, either: MIN, MAX,
// MATCH, MEDIAN, COUNTA, UPPER and the whole multi-criteria family. The app's
// own catalogue was offering five of them in autocomplete, so gTables was
// suggesting functions it could not run, and an imported Excel model lost every
// formula built on SUMIFS.
//
// Written here rather than pulled from formulajs because these are the
// functions where Excel's edge behaviour IS the specification — what a blank
// cell does to a criterion, what MIN of no numbers returns — and a wrapper
// around someone else's guess is harder to check than the rule itself. They are
// verified against Excel in tests/excelFns.test.ts.

import { FormulaError } from 'fast-formula-parser';

/**
 * One argument as the parser hands it over: a scalar for a literal or a single
 * cell, a 2-D array for a range. `omitted` marks a skipped argument, as in
 * `OFFSET(A1,1,,2)`.
 */
export interface Arg {
  value: unknown;
  isArray?: boolean;
  isRangeRef?: boolean;
  isCellRef?: boolean;
  omitted?: boolean;
}

export const EXCEL_FUNCTION_NAMES = [
  'MIN', 'MAX', 'MEDIAN', 'LARGE', 'SMALL', 'COUNTA', 'COUNTBLANK',
  'SUMIF', 'SUMIFS', 'COUNTIF', 'COUNTIFS', 'AVERAGEIF', 'AVERAGEIFS', 'MINIFS', 'MAXIFS',
  'MATCH', 'CHOOSE', 'SWITCH', 'UPPER', 'SUBSTITUTE', 'TEXTJOIN', 'OFFSET',
] as const;

export function makeExcelFunctions(): Record<string, (...args: never[]) => unknown> {
  const fns = {
    // -- Aggregates ---------------------------------------------------------

    /**
     * Excel returns 0, not an error, for MIN of nothing. Text and booleans
     * inside a range are ignored — a column of labels above a column of numbers
     * must not make MIN return the label.
     */
    MIN: (...args: Arg[]): number => {
      const values = numbersIn(args);
      return values.length === 0 ? 0 : Math.min(...values);
    },
    MAX: (...args: Arg[]): number => {
      const values = numbersIn(args);
      return values.length === 0 ? 0 : Math.max(...values);
    },
    MEDIAN: (...args: Arg[]): number => {
      const values = numbersIn(args).sort((a, b) => a - b);
      if (values.length === 0) throw FormulaError.NUM;
      const mid = values.length >> 1;
      return values.length % 2 === 1 ? values[mid]! : (values[mid - 1]! + values[mid]!) / 2;
    },
    LARGE: (array: Arg, k: Arg): number => nth(numbersIn([array]).sort((a, b) => b - a), k),
    SMALL: (array: Arg, k: Arg): number => nth(numbersIn([array]).sort((a, b) => a - b), k),

    /** Everything that is not an empty cell, text and booleans included. */
    COUNTA: (...args: Arg[]): number => {
      let n = 0;
      for (const arg of args) for (const v of flatten(arg)) if (!isBlank(v)) n++;
      return n;
    },
    COUNTBLANK: (range: Arg): number => {
      let n = 0;
      for (const v of flatten(range)) if (isBlank(v)) n++;
      return n;
    },

    // -- Conditional aggregates ---------------------------------------------
    //
    // The whole family, including the three the library DID implement. They are
    // overridden on purpose: SUMIF and SUMIFS answering differently about the
    // same blank cell — the library counts a blank as 0, Excel does not count
    // it at all — is the kind of disagreement nobody would think to check.

    SUMIFS: (sumRange: Arg, ...rest: Arg[]): number =>
      reduceIf('sum', sumRange, pairsOf(rest), sumRange),
    AVERAGEIFS: (averageRange: Arg, ...rest: Arg[]): number =>
      reduceIf('average', averageRange, pairsOf(rest), averageRange),
    MINIFS: (minRange: Arg, ...rest: Arg[]): number => reduceIf('min', minRange, pairsOf(rest), minRange),
    MAXIFS: (maxRange: Arg, ...rest: Arg[]): number => reduceIf('max', maxRange, pairsOf(rest), maxRange),
    COUNTIF: (range: Arg, criteria: Arg): number => reduceIf('count', range, [[range, criteria]], undefined),
    COUNTIFS: (...rest: Arg[]): number => {
      const pairs = pairsOf(rest);
      return reduceIf('count', pairs[0]?.[0] ?? { value: null }, pairs, undefined);
    },

    // -- Lookup -------------------------------------------------------------

    /**
     * The position of a value in a row or a column, 1-based.
     *
     * Type 0 is exact and is the one people mean; 1 (the default) wants the
     * array sorted ascending and finds the largest value at or below the
     * lookup, which is how a rate table works.
     */
    MATCH: (lookup: Arg, array: Arg, matchType?: Arg): number => {
      const target = scalarOf(lookup);
      const values = flatten(array);
      const type = matchType === undefined || matchType.omitted ? 1 : Math.sign(intOf(matchType, 'match_type'));

      if (type === 0) {
        const criterion = parseCriterion(target);
        for (let i = 0; i < values.length; i++) if (matches(values[i], criterion)) return i + 1;
        throw FormulaError.NA;
      }
      let found = -1;
      for (let i = 0; i < values.length; i++) {
        const v = values[i];
        if (isBlank(v)) continue;
        const c = compare(v, target);
        if (c === null) continue;
        if (type > 0 ? c <= 0 : c >= 0) found = i;
        else break;
      }
      if (found < 0) throw FormulaError.NA;
      return found + 1;
    },

    SWITCH: (expression: Arg, ...rest: Arg[]): unknown => {
      const subject = scalarOf(expression);
      let i = 0;
      for (; i + 1 < rest.length; i += 2) {
        if (compare(subject, scalarOf(rest[i]!)) === 0) return rest[i + 1]!.value;
      }
      // A lone trailing argument is the default.
      if (i < rest.length) return rest[i]!.value;
      throw FormulaError.NA;
    },

    // -- Text ---------------------------------------------------------------

    /** LOWER exists in the library. UPPER, somehow, does not. */
    UPPER: (text: Arg): string => textOf(text).toUpperCase(),

    SUBSTITUTE: (text: Arg, oldText: Arg, newText: Arg, instance?: Arg): string => {
      const source = textOf(text);
      const find = textOf(oldText);
      const replacement = textOf(newText);
      if (find === '') return source;
      if (instance === undefined || instance.omitted) return source.split(find).join(replacement);
      const which = intOf(instance, 'instance_num');
      if (which < 1) throw FormulaError.VALUE;
      let at = -1;
      for (let n = 0; n < which; n++) {
        at = source.indexOf(find, at + (n === 0 ? 0 : find.length));
        if (at < 0) return source;
      }
      return source.slice(0, at) + replacement + source.slice(at + find.length);
    },

    TEXTJOIN: (delimiter: Arg, ignoreEmpty: Arg, ...texts: Arg[]): string => {
      const sep = textOf(delimiter);
      const skipEmpty = boolOf(ignoreEmpty, true);
      const parts: string[] = [];
      for (const arg of texts) {
        for (const v of flatten(arg)) {
          const s = isBlank(v) ? '' : stringify(v);
          if (skipEmpty && s === '') continue;
          parts.push(s);
        }
      }
      return parts.join(sep);
    },
  };
  return fns as unknown as Record<string, (...args: never[]) => unknown>;
}

// ---------------------------------------------------------------------------
// Argument handling
// ---------------------------------------------------------------------------

function flatten(arg: Arg | undefined): unknown[] {
  if (arg === undefined || arg.omitted) return [];
  const out: unknown[] = [];
  const walk = (v: unknown): void => {
    if (Array.isArray(v)) {
      for (const x of v) walk(x);
      return;
    }
    out.push(v);
  };
  walk(arg.value);
  return out;
}

/** A range argument as rows of cells, whatever shape it arrived in. */
function grid(arg: Arg | undefined): unknown[][] {
  const value = arg?.value;
  if (!Array.isArray(value)) return [[value ?? null]];
  if (value.length === 0) return [[null]];
  return value.map((row) => (Array.isArray(row) ? row : [row]));
}

function numbersIn(args: readonly Arg[]): number[] {
  const out: number[] = [];
  for (const arg of args) {
    // A number typed straight into the formula counts even as text ("5"), the
    // same string inside a RANGE does not — Excel's rule, and the reason a
    // column of codes does not join the arithmetic.
    const fromRange = arg.isRangeRef === true || arg.isArray === true;
    for (const v of flatten(arg)) {
      if (typeof v === 'number' && Number.isFinite(v)) out.push(v);
      else if (!fromRange && typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) {
        out.push(Number(v));
      }
    }
  }
  return out;
}

function nth(sorted: readonly number[], k: Arg): number {
  const i = intOf(k, 'k');
  const value = sorted[i - 1];
  if (i < 1 || value === undefined) throw FormulaError.NUM;
  return value;
}

function scalarOf(arg: Arg | undefined): unknown {
  const value = arg?.value;
  if (Array.isArray(value)) {
    const first = (value as unknown[][])[0];
    return Array.isArray(first) ? (first[0] ?? null) : (first ?? null);
  }
  return value ?? null;
}

function intOf(arg: Arg | undefined, name: string): number {
  const v = scalarOf(arg);
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) throw new FormulaError('#VALUE!', `${name} must be a number`);
  return Math.trunc(n);
}

function textOf(arg: Arg | undefined): string {
  const v = scalarOf(arg);
  return isBlank(v) ? '' : stringify(v);
}

function boolOf(arg: Arg | undefined, fallback: boolean): boolean {
  const v = scalarOf(arg);
  if (v === null || v === undefined || v === '') return fallback;
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  return String(v).toUpperCase() !== 'FALSE';
}

function stringify(v: unknown): string {
  if (typeof v === 'boolean') return v ? 'TRUE' : 'FALSE';
  return String(v);
}

function isBlank(v: unknown): boolean {
  return v === null || v === undefined || v === '';
}

function pairsOf(args: readonly Arg[]): [Arg, Arg][] {
  const pairs: [Arg, Arg][] = [];
  for (let i = 0; i + 1 < args.length; i += 2) pairs.push([args[i]!, args[i + 1]!]);
  if (pairs.length === 0) throw FormulaError.VALUE;
  return pairs;
}

// ---------------------------------------------------------------------------
// Criteria
// ---------------------------------------------------------------------------

type Op = '=' | '<>' | '>' | '<' | '>=' | '<=';

interface Criterion {
  op: Op;
  value: unknown;
  /** Set when the criterion is text containing `*` or `?`. */
  wildcard?: RegExp;
}

const OP_RE = /^(<>|>=|<=|>|<|=)?([\s\S]*)$/;

/**
 * `">100"`, `"<>"`, `"North*"`, `42`, `TRUE` — Excel packs the operator into
 * the criterion string, and everything without one means "equals".
 */
export function parseCriterion(raw: unknown): Criterion {
  if (typeof raw === 'number' || typeof raw === 'boolean') return { op: '=', value: raw };
  if (raw === null || raw === undefined) return { op: '=', value: '' };

  const text = String(raw);
  const m = OP_RE.exec(text)!;
  const op = (m[1] ?? '=') as Op;
  const rest = m[2] ?? '';

  if (rest === '') return { op, value: '' };
  const upper = rest.toUpperCase();
  if (upper === 'TRUE' || upper === 'FALSE') return { op, value: upper === 'TRUE' };
  if (rest.trim() !== '' && Number.isFinite(Number(rest))) return { op, value: Number(rest) };
  if (/(^|[^~])[*?]/.test(rest)) return { op, value: rest, wildcard: wildcardRegex(rest) };
  return { op, value: rest };
}

function wildcardRegex(pattern: string): RegExp {
  let out = '';
  for (let i = 0; i < pattern.length; i++) {
    const ch = pattern[i]!;
    if (ch === '~' && (pattern[i + 1] === '*' || pattern[i + 1] === '?' || pattern[i + 1] === '~')) {
      out += escapeRegex(pattern[i + 1]!);
      i++;
    } else if (ch === '*') out += '[\\s\\S]*';
    else if (ch === '?') out += '[\\s\\S]';
    else out += escapeRegex(ch);
  }
  return new RegExp(`^${out}$`, 'i');
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Does one cell satisfy one criterion?
 *
 * The blank rule is the part worth stating: an EMPTY cell never satisfies a
 * comparison, so `">=0"` does not count the empty half of a column, and
 * `"<>North"` does count it. The library's own SUMIF reads a blank as 0 and
 * therefore counts it — which is why the family is re-implemented here rather
 * than half-borrowed.
 */
export function matches(value: unknown, criterion: Criterion): boolean {
  const blank = isBlank(value);
  if (blank) {
    if (criterion.op === '=') return criterion.value === '';
    if (criterion.op === '<>') return criterion.value !== '';
    return false;
  }
  if (criterion.wildcard) {
    const hit = criterion.wildcard.test(stringify(value));
    return criterion.op === '<>' ? !hit : hit;
  }
  const order = compare(value, criterion.value);
  if (order === null) return criterion.op === '<>';
  switch (criterion.op) {
    case '=': return order === 0;
    case '<>': return order !== 0;
    case '>': return order > 0;
    case '<': return order < 0;
    case '>=': return order >= 0;
    case '<=': return order <= 0;
  }
}

/**
 * Excel's comparison order across types: numbers, then text, then booleans.
 * Null means "not comparable" — two values of different types are never equal,
 * and ordering them is nobody's intention.
 */
function compare(a: unknown, b: unknown): number | null {
  if (typeof a === 'number' && typeof b === 'number') return a === b ? 0 : a < b ? -1 : 1;
  if (typeof a === 'boolean' && typeof b === 'boolean') return a === b ? 0 : a ? 1 : -1;
  if (typeof a === 'string' && typeof b === 'string') {
    // Text comparison is case-INSENSITIVE in Excel: "north" = "North".
    const x = a.toLowerCase();
    const y = b.toLowerCase();
    return x === y ? 0 : x < y ? -1 : 1;
  }
  if (isBlank(a) || isBlank(b)) return null;
  return null;
}

// ---------------------------------------------------------------------------
// The conditional-aggregate engine
// ---------------------------------------------------------------------------

type Reduction = 'sum' | 'count' | 'average' | 'min' | 'max';

/**
 * One walk for the whole family.
 *
 * `shape` is the range whose dimensions everything else is checked against —
 * Excel refuses criteria ranges of a different size rather than guessing which
 * cell lines up with which.
 */
function reduceIf(
  reduction: Reduction,
  shape: Arg,
  pairs: readonly [Arg, Arg][],
  valueRange: Arg | undefined
): number {
  const template = grid(shape);
  const rows = template.length;
  const cols = template[0]?.length ?? 0;

  const tests = pairs.map(([range, criteria]) => {
    const cells = grid(range);
    if (cells.length !== rows || (cells[0]?.length ?? 0) !== cols) throw FormulaError.VALUE;
    return { cells, criterion: parseCriterion(scalarOf(criteria)) };
  });
  const values = valueRange === undefined || valueRange.omitted ? null : grid(valueRange);
  // Excel refuses a value range of a different size rather than guessing which
  // cell pairs with which — the same rule it applies to the criteria ranges.
  if (values !== null && (values.length !== rows || (values[0]?.length ?? 0) !== cols)) {
    throw FormulaError.VALUE;
  }

  let sum = 0;
  let count = 0;
  let best: number | null = null;

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      let ok = true;
      for (const test of tests) {
        if (!matches(test.cells[r]![c], test.criterion)) {
          ok = false;
          break;
        }
      }
      if (!ok) continue;
      if (reduction === 'count') {
        count++;
        continue;
      }
      const raw = values === null ? null : values[r]?.[c];
      if (typeof raw !== 'number' || !Number.isFinite(raw)) continue;
      sum += raw;
      count++;
      if (best === null) best = raw;
      else if (reduction === 'min') best = Math.min(best, raw);
      else if (reduction === 'max') best = Math.max(best, raw);
    }
  }

  switch (reduction) {
    case 'sum':
      return sum;
    case 'count':
      return count;
    case 'average':
      if (count === 0) throw FormulaError.DIV0;
      return sum / count;
    case 'min':
    case 'max':
      // Excel answers 0, not an error, when nothing matched.
      return best ?? 0;
  }
}

// ---------------------------------------------------------------------------
// The four the parser insists on calling with its own context
//
// SUMIF, AVERAGEIF, CHOOSE and OFFSET are named inside the library: whatever is
// registered under those names is called as `fn(context, ...args)`, and the
// first three of those four also get their arguments UNRESOLVED. That is not a
// choice this file can make, so it is one this file has to know about — a
// value-shaped implementation registered under those names silently reads the
// context object as its first argument, which is a wrong answer rather than an
// error.
// ---------------------------------------------------------------------------

/** What the parser hands a function that asked NOT to have its refs resolved. */
interface RefArg {
  ref?: {
    sheet?: string;
    row?: number;
    col?: number;
    from?: { row: number; col: number };
    to?: { row: number; col: number };
  };
}

interface RefContext {
  utils: { extractRefValue(arg: unknown): { val: unknown; isArray: boolean } };
}

/** Registered so their arguments arrive as references rather than values. */
export const EXCEL_UNRESOLVED_ARG_NAMES = ['OFFSET'] as const;

/**
 * OFFSET returns a RANGE, not a number — `SUM(OFFSET(B2,0,0,12,1))` has to
 * reach twelve cells — so it is the one function here that cannot be written
 * against values. It takes its first argument as a reference and hands one
 * back, which the parser then reads for whoever asked.
 *
 * See engine.ts for the two lines of wiring that make the reference arrive
 * unresolved, and for why a formula holding one recalculates on every edit.
 */
export function makeExcelContextFunctions(): Record<string, (context: never, ...args: never[]) => unknown> {
  const fns = {
    /** Values, but called with the context because the library says so. */
    CHOOSE: (_context: RefContext, index: Arg, ...choices: Arg[]): unknown => {
      const i = intOf(index, 'index_num');
      const chosen = choices[i - 1];
      if (i < 1 || chosen === undefined || chosen.omitted) throw FormulaError.VALUE;
      return chosen.value;
    },

    /**
     * The library keeps SUMIF and AVERAGEIF on its unresolved-argument list, so
     * theirs arrive as references. Resolved here, then handed to the same walk
     * every other conditional aggregate uses.
     */
    SUMIF: (context: RefContext, range: RefArg, criteria: RefArg, sumRange?: RefArg): number => {
      const cells = resolve(context, range);
      // Two-argument SUMIF adds up the range it tested.
      const values = sumRange == null ? cells : resolve(context, sumRange);
      return reduceIf('sum', cells, [[cells, resolve(context, criteria)]], values);
    },
    AVERAGEIF: (context: RefContext, range: RefArg, criteria: RefArg, averageRange?: RefArg): number => {
      const cells = resolve(context, range);
      const values = averageRange == null ? cells : resolve(context, averageRange);
      return reduceIf('average', cells, [[cells, resolve(context, criteria)]], values);
    },

    OFFSET: (context: RefContext, reference: RefArg, rows: unknown, cols: unknown, height?: unknown, width?: unknown) => {
      const base = reference?.ref;
      if (!base) throw FormulaError.VALUE;
      const from = base.from ?? { row: base.row ?? 0, col: base.col ?? 0 };
      const to = base.to ?? from;
      if (!from.row || !from.col) throw FormulaError.VALUE;

      const dRow = contextInt(context, rows, 'rows');
      const dCol = contextInt(context, cols, 'cols');
      const h = height === undefined || height === null ? to.row - from.row + 1 : contextInt(context, height, 'height');
      const w = width === undefined || width === null ? to.col - from.col + 1 : contextInt(context, width, 'width');
      if (h <= 0 || w <= 0) throw FormulaError.REF;

      const row = from.row + dRow;
      const col = from.col + dCol;
      // Off the top or the left of the sheet is #REF!, exactly as in Excel.
      if (row < 1 || col < 1) throw FormulaError.REF;

      if (h === 1 && w === 1) return { ref: { sheet: base.sheet, row, col } };
      return {
        ref: { sheet: base.sheet, from: { row, col }, to: { row: row + h - 1, col: col + w - 1 } },
      };
    },
  };
  return fns as unknown as Record<string, (context: never, ...args: never[]) => unknown>;
}

/** A reference argument as the value behind it, in the shape the rest of the file speaks. */
function resolve(context: RefContext, arg: unknown): Arg {
  if (arg === null || arg === undefined) return { value: null, omitted: true };
  const { val, isArray } = context.utils.extractRefValue(arg);
  return { value: val ?? null, isArray, isRangeRef: isArray };
}

function contextInt(context: RefContext, arg: unknown, name: string): number {
  if (arg === undefined || arg === null) return 0;
  const { val } = context.utils.extractRefValue(arg);
  const first = Array.isArray(val) ? (val as unknown[][])[0]?.[0] : val;
  if (first === null || first === undefined || first === '') return 0;
  const n = typeof first === 'number' ? first : Number(first);
  if (!Number.isFinite(n)) throw new FormulaError('#VALUE!', `${name} must be a number`);
  return Math.trunc(n);
}
